import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MessageCircle, Phone, Mail, Trash2, User, Bot, Search, RefreshCw, ArrowLeft, Clock, Hash, Flag, Reply, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { sanitizeHtml } from '../../lib/sanitize';
import { groupByDate, timeLabel, formatPhone } from '../../lib/dateBuckets';
import { EmailComposer } from './email/EmailComposer';
import { SelectionBar, RowCheckbox, RowDeleteButton } from './email/SelectionBar';
import { fmtWhen } from './email/SchedulePicker';
import { nameFromEmail } from '../../lib/emailTemplates';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const firstUserLine = (s) => {
  const m = s.messages?.find((x) => x.role === 'user' && x.content?.trim());
  return (m?.content || s.messages?.[0]?.content || 'Conversation').replace(/<[^>]+>/g, '').trim();
};
const whoLabel = (s) => s.contact_info?.emails?.[0] || (s.contact_info?.phones?.[0] ? formatPhone(s.contact_info.phones[0]) : 'Anonymous visitor');

const highlightContactInfo = (text) => text
  .replace(/([+]?[(]?[0-9]{1,3}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9})/g, '<span class="bg-emerald-100 text-emerald-900 px-1 rounded font-medium">$1</span>')
  .replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<span class="bg-blue-100 text-blue-900 px-1 rounded font-medium">$1</span>');

const SessionRow = ({ s, active, onClick, selected, onSelect, onDelete }) => {
  const hasContact = s.contact_info?.has_contact_info;
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === 'Enter' && onClick()} aria-current={active ? 'true' : undefined}
      className={`group w-full text-left px-3 py-2 border-l-2 transition-colors cursor-pointer ${active ? 'bg-amber-50 border-amber-500' : selected ? 'bg-amber-50/50 border-amber-300' : 'border-transparent hover:bg-neutral-50'}`}
      data-testid={`chat-session-${s.session_id}`}>
      <div className="flex items-center gap-2">
        <RowCheckbox checked={selected} onChange={onSelect} testId={`chat-select-${s.session_id}`} />
        {s.needs_follow_up
          ? <Flag className="w-3 h-3 text-red-500 fill-red-500 shrink-0" data-testid={`chat-flag-${s.session_id}`} />
          : <span className={`w-2 h-2 rounded-full shrink-0 ${hasContact ? 'bg-emerald-500' : 'bg-neutral-300'}`} />}
        <span className={`text-xs truncate flex-1 ${hasContact ? 'font-semibold text-neutral-900' : 'font-medium text-neutral-700'}`}>{whoLabel(s)}</span>
        <span className="text-[10px] text-neutral-400 shrink-0 group-hover:hidden">{timeLabel(s.updated_at)}</span>
        <RowDeleteButton onClick={onDelete} testId={`chat-row-delete-${s.session_id}`} title="Delete chat" />
      </div>
      <p className="mt-0.5 text-[11px] text-neutral-500 truncate pl-10">{firstUserLine(s)}</p>
      <p className="mt-0.5 text-[10px] text-neutral-400 pl-10 flex items-center gap-2">
        {s.message_count} messages
        {s.replies?.length > 0 && <span className="inline-flex items-center gap-0.5 text-emerald-600"><Reply className="w-2.5 h-2.5" />replied</span>}
        {s.scheduled_reply && <span className="inline-flex items-center gap-0.5 text-amber-700" data-testid={`chat-scheduled-${s.session_id}`}><Clock className="w-2.5 h-2.5" />scheduled</span>}
      </p>
    </div>
  );
};

const ChatReplyComposer = ({ open, onOpenChange, session, token, onSent, onScheduled }) => {
  const { user } = useAuth();
  const [opts, setOpts] = useState({ include_transcript: true, resolve_follow_up: true });
  useEffect(() => { if (open) setOpts({ include_transcript: true, resolve_follow_up: true }); }, [open]);
  const email = session?.contact_info?.emails?.[0] || '';
  const initial = useMemo(() => ({ to: email ? [email] : [], subject: 'Following up on your chat with Apollo Shades', html: '<p>Hi there,</p><p>Thanks for chatting with us. </p>' }), [email]);
  const send = async (p) => {
    const { data } = await axios.post(`${API_URL}/admin/chat-sessions/${session.session_id}/reply`,
      { to_email: p.to[0], cc: [...p.to.slice(1), ...p.cc, ...p.bcc], subject: p.subject, body: p.text, body_html: p.html, template_id: p.template_id, ...opts },
      { headers: { Authorization: `Bearer ${token}` } });
    toast.success(`Reply sent to ${p.to[0]}`);
    onSent(data.reply, opts.resolve_follow_up);
  };
  const schedule = async (p, sendAt) => {
    const { data } = await axios.post(`${API_URL}/admin/emails/schedule`,
      { kind: 'chat_reply', send_at: sendAt, session_id: session.session_id, payload: { to_email: p.to[0], cc: [...p.to.slice(1), ...p.cc, ...p.bcc], subject: p.subject, body: p.text, body_html: p.html, template_id: p.template_id, ...opts } },
      { headers: { Authorization: `Bearer ${token}` } });
    onScheduled({ id: data.id, send_at: data.send_at, subject: data.subject });
  };
  if (!session) return null;
  return (
    <EmailComposer open={open} onOpenChange={onOpenChange} token={token} initial={initial} onSend={send} onSchedule={schedule} allowDraft={false}
      title={`Reply to ${whoLabel(session)}`} sendLabel="Send reply" agentName={user?.name || user?.email || 'Apollo Shades'}
      placeholders={{ customer_name: nameFromEmail(email) }}
      extras={(
        <div className="flex flex-wrap gap-3 text-[11px] text-neutral-600" data-testid="chat-reply-options">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={opts.include_transcript} onChange={(e) => setOpts({ ...opts, include_transcript: e.target.checked })} className="accent-amber-600" data-testid="chat-reply-transcript" />Quote transcript</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={opts.resolve_follow_up} onChange={(e) => setOpts({ ...opts, resolve_follow_up: e.target.checked })} className="accent-amber-600" data-testid="chat-reply-resolve" />Clear follow-up</label>
        </div>
      )} />
  );
};

const Thread = ({ s, onDelete, onBack, onToggleFollowUp, onReply }) => (
  <div className="flex flex-col h-full" data-testid="chat-thread">
    <div className="flex items-start gap-3 px-4 py-2.5 border-b border-neutral-200 bg-white">
      <button type="button" onClick={onBack} className="lg:hidden p-1.5 -ml-1 rounded-md text-neutral-500 hover:bg-neutral-100" data-testid="chat-thread-back" aria-label="Back to list"><ArrowLeft className="w-4 h-4" /></button>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${s.contact_info?.has_contact_info ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500'}`}><MessageCircle className="w-3.5 h-3.5" /></div>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-neutral-900 truncate flex items-center gap-2" data-testid="chat-thread-title">
          {whoLabel(s)}
          {s.needs_follow_up && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-1.5 py-px" data-testid="chat-thread-followup-badge"><Flag className="w-2.5 h-2.5" />Needs follow-up</span>}
          {s.scheduled_reply && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-px" data-testid="chat-thread-scheduled-badge"><Clock className="w-2.5 h-2.5" />Reply scheduled for {fmtWhen(s.scheduled_reply.send_at)}</span>}
        </h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-neutral-500 mt-0.5">
          <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(s.updated_at).toLocaleString()}</span>
          <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" />{s.message_count} messages</span>
          {s.contact_info?.phones?.map((p) => <a key={p} href={`tel:${p.replace(/\D/g, '')}`} className="inline-flex items-center gap-1 text-emerald-700 hover:underline"><Phone className="w-3 h-3" />{formatPhone(p)}</a>)}
          {s.contact_info?.emails?.map((e) => <a key={e} href={`mailto:${e}`} className="inline-flex items-center gap-1 text-blue-700 hover:underline"><Mail className="w-3 h-3" />{e}</a>)}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant={s.needs_follow_up ? 'default' : 'outline'} size="sm" onClick={onToggleFollowUp} className={`h-8 text-xs gap-1.5 ${s.needs_follow_up ? 'bg-red-600 hover:bg-red-700' : ''}`} data-testid="chat-followup-toggle">
          <Flag className={`w-3.5 h-3.5 ${s.needs_follow_up ? 'fill-white' : ''}`} />{s.needs_follow_up ? 'Flagged' : 'Follow-up'}
        </Button>
        <Button size="sm" onClick={onReply} className="h-8 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700" data-testid="chat-reply-btn"><Reply className="w-3.5 h-3.5" />Reply</Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(s.session_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0" data-testid="chat-thread-delete"><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
    {s.follow_up_note && <div className="px-4 py-1.5 text-[11px] text-red-800 bg-red-50 border-b border-red-100" data-testid="chat-followup-note">Note: {s.follow_up_note}</div>}
    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-[linear-gradient(180deg,#fafaf9,#f5f5f4)]">
      {s.messages?.map((msg, idx) => {
        const mine = msg.role !== 'user';
        return (
          <div key={msg.id || msg.timestamp || `msg-${idx}`} className={`flex gap-2 ${mine ? 'flex-row-reverse' : ''}`} data-testid={`chat-msg-${idx}`}>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${mine ? 'bg-neutral-900 text-white' : 'bg-amber-100 text-amber-700'}`}>{mine ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}</div>
            <div className={`max-w-[62%] flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
              <div className="text-[10px] text-neutral-400 mb-0.5">{mine ? 'Apollo AI' : 'Customer'}{msg.timestamp ? ` · ${new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}</div>
              <div className={`text-xs px-3 py-2 shadow-sm leading-relaxed ${mine ? 'bg-white border border-neutral-200 rounded-2xl rounded-tr-sm text-neutral-800' : 'bg-amber-600 text-white rounded-2xl rounded-tl-sm'}`}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(mine ? msg.content : highlightContactInfo(msg.content || '')) }} />
            </div>
          </div>
        );
      })}
      {s.replies?.map((r) => (
        <div key={r.id} className="flex gap-2 flex-row-reverse" data-testid={`chat-reply-${r.id}`}>
          <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 bg-emerald-600 text-white"><Mail className="w-3 h-3" /></div>
          <div className="max-w-[62%] flex flex-col items-end">
            <div className="text-[10px] text-neutral-400 mb-0.5">Email reply · {r.sent_by} · {new Date(r.sent_at).toLocaleString()}</div>
            <div className="text-xs px-3 py-2 shadow-sm leading-relaxed bg-emerald-50 border border-emerald-200 rounded-2xl rounded-tr-sm text-neutral-800 whitespace-pre-wrap">
              <p className="font-medium text-emerald-800 mb-1">{r.subject}</p>{r.body_html ? <div className="[&_p]:my-1 whitespace-normal" dangerouslySetInnerHTML={{ __html: sanitizeHtml(r.body_html) }} /> : r.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const ChatHistoryTab = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [replyOpen, setReplyOpen] = useState(false);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/chat-sessions`, { headers: { Authorization: `Bearer ${token}` } });
      setSessions(response.data);
    } catch { toast.error('Failed to load chat sessions'); } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { if (token) fetchSessions(); }, [token, fetchSessions]);

  const patchSession = (id, patch) => setSessions((prev) => prev.map((s) => (s.session_id === id ? { ...s, ...patch } : s)));

  const deleteSession = async (sessionId) => {
    if (!window.confirm('Delete this chat session?')) return;
    try {
      await axios.delete(`${API_URL}/admin/chat-sessions/${sessionId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Session deleted');
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      if (activeId === sessionId) setActiveId(null);
    } catch { toast.error('Failed to delete session'); }
  };

  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const deleteSelected = async () => {
    const ids = [...selected];
    if (!window.confirm(`Delete ${ids.length} chat${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API_URL}/admin/chat-sessions/bulk-delete`, { session_ids: ids }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${ids.length} chat${ids.length === 1 ? '' : 's'} deleted`);
      setSessions((prev) => prev.filter((s) => !selected.has(s.session_id)));
      if (selected.has(activeId)) setActiveId(null);
      setSelected(new Set());
    } catch { toast.error('Could not delete the selected chats'); } finally { setBulkBusy(false); }
  };

  const toggleFollowUp = async (s) => {
    const next = !s.needs_follow_up;
    const note = next ? window.prompt('Follow-up note (optional):', s.follow_up_note || '') : null;
    if (next && note === null) return;
    try {
      const { data } = await axios.patch(`${API_URL}/admin/chat-sessions/${s.session_id}/follow-up`, { needs_follow_up: next, note }, { headers: { Authorization: `Bearer ${token}` } });
      patchSession(s.session_id, data);
      toast.success(next ? 'Marked for follow-up' : 'Follow-up cleared');
    } catch { toast.error('Could not update follow-up'); }
  };

  const filtered = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    return sessions
      .filter((s) => filter === 'all' || (filter === 'follow-up' ? !!s.needs_follow_up : (filter === 'with-contact') === !!s.contact_info?.has_contact_info))
      .filter((s) => !needle || s.messages?.some((m) => m.content?.toLowerCase().includes(needle)) || s.contact_info?.emails?.some((e) => e.toLowerCase().includes(needle)) || s.contact_info?.phones?.some((p) => p.replace(/\D/g, '').includes(needle.replace(/\D/g, '') || '§')))
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
  }, [sessions, filter, searchTerm]);
  const groups = useMemo(() => groupByDate(filtered, (s) => s.updated_at), [filtered]);

  const active = sessions.find((s) => s.session_id === activeId) || null;
  const withContact = sessions.filter((s) => s.contact_info?.has_contact_info).length;
  const flagged = sessions.filter((s) => s.needs_follow_up).length;
  const chips = [['all', 'All', sessions.length], ['follow-up', 'Follow-up', flagged], ['with-contact', 'With contact', withContact], ['no-contact', 'Anonymous', sessions.length - withContact]];

  return (
    <div className="-m-4 sm:-m-6 h-[calc(100vh-11rem)] min-h-[520px] flex overflow-hidden rounded-xl" data-testid="chat-history">
      <aside className={`w-full lg:w-[320px] xl:w-[360px] shrink-0 border-r border-neutral-200 flex flex-col bg-white ${active ? 'hidden lg:flex' : 'flex'}`} data-testid="chat-list">
        <div className="px-3 pt-3 pb-2 border-b border-neutral-200 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-neutral-900">Chat History</h3>
              <p className="text-[11px] text-neutral-500">{sessions.length} conversations · {flagged} need follow-up</p>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchSessions} className="h-7 w-7 p-0" title="Refresh" data-testid="chat-refresh-btn"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search messages, email, phone…" className="w-full h-8 pl-8 pr-2 rounded-md bg-neutral-100 border border-transparent focus:bg-white focus:border-amber-400 outline-none text-xs" data-testid="chat-search-input" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {chips.map(([v, label, n]) => (
              <button key={v} type="button" onClick={() => setFilter(v)} className={`h-6 px-2 rounded-full text-[11px] border transition-colors inline-flex items-center gap-1 ${filter === v ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600 hover:border-neutral-400'}`} data-testid={`chat-filter-${v}`}>
                {v === 'follow-up' && <Flag className={`w-3 h-3 ${filter === v ? '' : 'text-red-500'}`} />}{label} <span className="opacity-60">{n}</span>
              </button>
            ))}
          </div>
        </div>
        <SelectionBar count={selected.size} total={filtered.length} onSelectAll={() => setSelected(new Set(filtered.map((s) => s.session_id)))} onClear={() => setSelected(new Set())} onDelete={deleteSelected} busy={bulkBusy} noun="chat" />
        <div className="flex-1 overflow-y-auto">
          {loading && sessions.length === 0 ? (
            <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" /></div>
          ) : groups.length === 0 ? (
            <div className="text-center py-12 text-neutral-500 text-sm" data-testid="chat-empty"><MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-30" />No conversations found</div>
          ) : groups.map((g) => (
            <section key={g.key} data-testid={`chat-group-${g.key}`}>
              <h4 className="sticky top-0 z-10 bg-neutral-50/95 backdrop-blur px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-500 border-y border-neutral-100">{g.label} <span className="text-neutral-400 font-normal">· {g.items.length}</span></h4>
              <div className="divide-y divide-neutral-100">{g.items.map((s) => <SessionRow key={s.session_id} s={s} active={s.session_id === activeId} onClick={() => setActiveId(s.session_id)} selected={selected.has(s.session_id)} onSelect={() => toggleSelect(s.session_id)} onDelete={() => deleteSession(s.session_id)} />)}</div>
            </section>
          ))}
        </div>
      </aside>

      <section className={`flex-1 min-w-0 bg-neutral-50 ${active ? 'flex' : 'hidden lg:flex'} flex-col`} data-testid="chat-reading-pane">
        {active ? (
          <Thread s={active} onDelete={deleteSession} onBack={() => setActiveId(null)} onToggleFollowUp={() => toggleFollowUp(active)} onReply={() => setReplyOpen(true)} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-neutral-400 p-8 text-center" data-testid="chat-empty-pane">
            <MessageCircle className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm font-medium text-neutral-600">Select a conversation</p>
            <p className="text-xs">Pick a chat on the left to read the full transcript.</p>
          </div>
        )}
      </section>

      <ChatReplyComposer open={replyOpen} onOpenChange={setReplyOpen} session={active} token={token}
        onSent={(reply, resolve) => active && patchSession(active.session_id, { replies: [...(active.replies || []), reply], scheduled_reply: null, ...(resolve ? { needs_follow_up: false, follow_up_note: null } : {}) })}
        onScheduled={(sr) => active && patchSession(active.session_id, { scheduled_reply: sr })} />
      {flagged > 0 && filter !== 'follow-up' && !active && <span className="sr-only"><CheckCircle2 /></span>}
    </div>
  );
};

export default ChatHistoryTab;
